import {
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  HStack,
  Spinner,
  Text,
} from "@chakra-ui/react";
import { CopiedFromOriginalNote } from "../../CopiedFromOriginalNote";
import { skipToken } from "@reduxjs/toolkit/query";
import { debounce, uniqBy } from "lodash";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  bffApi,
  ContactPersonDto,
  ContactSubjectType,
  FrameworkRole,
  PersonDto,
  PhoneNumberDto,
  useGetOrganizationQuery,
  useListContactPersonsQuery,
  useListPersonsQuery,
} from "../../../../../../autogen/bff-api";
import { supplierContactPersons } from "../../../../../../common/contactPersons/supplierContactPersons";
import { ContactPersonForm } from "../../../../../../common/ContactPersonForm";
import { displayPhoneNumber } from "../../../../../../common/formatting/displayPhoneNumber";
import { SingleSelector } from "../../../../../../common/input/Selector/SingleSelector";
import { useAppDispatch } from "../../../../../../common/redux/hooks";
import { containsError } from "../../../../../../common/redux/reducers/contractReducer";
import { editContractThunk } from "../../../../../../common/redux/thunks/contract/edit-contract-thunk";
import { useApiError } from "../../../../../../common/errors/useApiError";
import { useSub } from "../../../../../../common/subscription/useSub";
import { useConfirmationModal } from "../../../../../../common/useConfirmationModal";
import { displayPersonNameWithEmail } from "../../../../../../common/persons/displayPersonName";
import { useContractState } from "../../../useContractState";
import { RemoveDataFieldButton } from "../../../../../../common/input/RemoveDataFieldButton";

interface Props {
  removeDataField?: () => Promise<void>;
  isRequiredButNotProvided: boolean;
}

const PERSON_PREFIX = "person:";
const CONTACT_PREFIX = "contact:";
const ACCOUNTS_LIMIT = 100;
const SEARCH_LIMIT = 10;

const contactLabel = (name: string | null | undefined, email: string) =>
  name?.trim() ? `${name} (${email})` : email;

const matchesSearch = (contact: ContactPersonDto, search: string) => {
  const needle = search.trim().toLowerCase();
  return !needle || contact.email.toLowerCase().includes(needle) || !!contact.name?.toLowerCase().includes(needle);
};

export const CounterpartyContactPerson = ({ removeDataField, isRequiredButNotProvided }: Props) => {
  const { t } = useTranslation();
  const contractState = useContractState();
  const { canUpdateContactPersons: canManageContacts } = useSub();
  const [isLoading, setIsLoading] = useState(false);
  const errorMessage = containsError(contractState, "EditCounterpartyContactPerson") ? t("Update failed") : null;
  const dispatch = useAppDispatch();
  const { trigger } = useApiError();
  const { show: confirm, modal: confirmationModal } = useConfirmationModal();

  const counterpartyOrganization = contractState.contract.counterparty?.organization;
  const counterpartyOrganizationEntry = contractState.contract.counterparty?.organizationEntry;
  const counterpartyContactPerson = contractState.contract.dataFields.counterpartyContactPerson;

  const subject = counterpartyOrganization
    ? { subjectType: ContactSubjectType.RegisteredOrganization, subjectId: counterpartyOrganization.id }
    : counterpartyOrganizationEntry
    ? { subjectType: ContactSubjectType.OrganizationEntry, subjectId: counterpartyOrganizationEntry.id }
    : undefined;

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debounceSearch = useCallback(debounce(setDebouncedSearch, 300), []);

  const savedContactsQuery = useListContactPersonsQuery(
    subject ? { ...subject, name: debouncedSearch || undefined, limit: SEARCH_LIMIT } : skipToken
  );
  const savedContacts = savedContactsQuery.data;
  const organizationQuery = useGetOrganizationQuery(
    counterpartyOrganization ? { orgId: counterpartyOrganization.id } : skipToken
  );
  const organization = organizationQuery.data;
  const linkedEntries = (organization?.linkedEntries ?? []).map((entry) => ({
    contactPersons: entry.contactPersons?.filter((contact) => matchesSearch(contact, debouncedSearch)),
  }));
  const contacts = supplierContactPersons(savedContacts?.contacts ?? [], linkedEntries);

  const frameworkRole = contractState.contract.frameworkRole;
  const counterpartyIsLocked = !!frameworkRole && frameworkRole !== FrameworkRole.Standalone;
  const sisterOrganizationIds = counterpartyIsLocked
    ? []
    : (organization?.sameLegalEntityIds ?? []).filter(
        (id) => id !== counterpartyOrganization?.id && id !== contractState.contract.owningOrganizationId
      );
  const sameLegalEntityIds = counterpartyOrganization ? [counterpartyOrganization.id, ...sisterOrganizationIds] : [];
  const canListPersons = !!counterpartyOrganization && !organizationQuery.isLoading;

  const personsQuery = useListPersonsQuery(
    canListPersons
      ? { orgIds: sameLegalEntityIds, name: debouncedSearch || undefined, limit: SEARCH_LIMIT }
      : skipToken
  );
  const { data: personsData, isFetching: isLoadingPersons } = personsQuery;
  const registeredPersons = uniqBy(personsData?.persons ?? [], (person) => person.id);
  const accountsQuery = useListPersonsQuery(
    canListPersons ? { orgIds: sameLegalEntityIds, limit: ACCOUNTS_LIMIT } : skipToken
  );
  const accountsData = accountsQuery.data;
  const failedQueries = [savedContactsQuery, organizationQuery, personsQuery, accountsQuery].filter((query) => query.isError);
  const isRetrying = failedQueries.some((query) => query.isFetching);
  const retryFailedQueries = () => failedQueries.forEach((query) => query.refetch());
  const hasMoreResults = (savedContacts?.contacts.length ?? 0) >= SEARCH_LIMIT || registeredPersons.length >= SEARCH_LIMIT;

  const eventHandler = useCallback(
    async (values: {
      personId?: string | null;
      fullName?: string | null;
      email?: string | null;
      phoneNumber?: PhoneNumberDto | null;
    }) => {
      setIsLoading(true);
      await dispatch(
        editContractThunk({
          command: {
            type: "EditCounterpartyContactPerson",
            value: {
              fullName: values.fullName ?? null,
              email: values.email ?? null,
              phoneNumber: values.phoneNumber ?? null,
              personId: values.personId ?? null,
            },
          },
        })
      );
      setIsLoading(false);
    },
    [dispatch]
  );

  const selectContact = (contact: ContactPersonDto) =>
    eventHandler({ fullName: contact.name, email: contact.email, phoneNumber: contact.phoneNumber });

  const registeredEmails = new Set(
    [...(accountsData?.persons ?? []), ...registeredPersons].map((p) => p.email.trim().toLowerCase())
  );
  const options = [
    {
      label: t("Persons with a SourceMagnet account"),
      options: registeredPersons.map((p) => ({ value: `${PERSON_PREFIX}${p.id}`, label: displayPersonNameWithEmail(p) })),
    },
    {
      label: t("Contact persons registered by your organization"),
      options: contacts
        .filter((c) => !registeredEmails.has(c.email.trim().toLowerCase()))
        .map((c) => ({ value: `${CONTACT_PREFIX}${c.email}`, label: contactLabel(c.name, c.email) })),
    },
  ].filter((group) => group.options.length > 0);

  const legacyParts = [
    counterpartyContactPerson?.fullName,
    counterpartyContactPerson?.phoneNumber ? displayPhoneNumber(counterpartyContactPerson.phoneNumber) : undefined,
  ].filter((part): part is string => !!part);

  const selectedOption = counterpartyContactPerson?.person
    ? {
        value: `${PERSON_PREFIX}${counterpartyContactPerson.person.id}`,
        label: displayPersonNameWithEmail(counterpartyContactPerson.person),
      }
    : counterpartyContactPerson?.email
    ? {
        value: `${CONTACT_PREFIX}${counterpartyContactPerson.email}`,
        label: contactLabel(counterpartyContactPerson.fullName, counterpartyContactPerson.email),
      }
    : legacyParts.length > 0
    ? { value: "legacy", label: `${legacyParts.join(" · ")} (${t("No email")})` }
    : undefined;

  const organizationOf = async (person: PersonDto) => {
    for (const orgId of sameLegalEntityIds) {
      const members = await dispatch(
        bffApi.endpoints.listPersons.initiate({ orgIds: [orgId], name: person.email, limit: SEARCH_LIMIT })
      ).unwrap();
      if (members.persons.some((member) => member.id === person.id)) return orgId;
    }
  };

  const selectPerson = async (personId: string) => {
    const person = [...registeredPersons, ...(accountsData?.persons ?? [])].find((p) => p.id === personId);
    if (!person || !counterpartyOrganization || sisterOrganizationIds.length === 0) return eventHandler({ personId });
    try {
      const orgId = await organizationOf(person);
      if (!orgId || orgId === counterpartyOrganization.id) return eventHandler({ personId });
      const sister = await dispatch(bffApi.endpoints.getOrganization.initiate({ orgId })).unwrap();
      confirm({
        title: `${t("Change the counterparty?")}`,
        description: t(
          "{{person}} is registered in {{organization}}. The contract will be linked to {{organization}}, and {{current}} loses its access to the contract as counterparty.",
          { person: displayPersonNameWithEmail(person), organization: sister.name, current: counterpartyOrganization.name }
        ),
        confirmButtonText: t("Change counterparty") ?? undefined,
        size: "lg",
        onConfirmed: () => eventHandler({ personId }),
      });
    } catch (e) {
      trigger(e);
    }
  };

  const onSelect = (option: { value: string } | null) => {
    if (!option) return eventHandler({});
    if (option.value.startsWith(PERSON_PREFIX)) {
      return selectPerson(option.value.slice(PERSON_PREFIX.length));
    }
    const contact = contacts.find((c) => c.email === option.value.slice(CONTACT_PREFIX.length));
    if (contact) selectContact(contact);
  };

  return (
    <Box position="relative">
      {confirmationModal}
      <Box position="absolute" right="0" top="0" zIndex="1">
        {removeDataField && <RemoveDataFieldButton removeDataField={removeDataField} />}
      </Box>
      <FormControl isInvalid={errorMessage !== null} isRequired={isRequiredButNotProvided}>
        <FormLabel py="2">
          {t("Counterparty contact person")} {isLoading && <Spinner size="xs" />}
        </FormLabel>
        {subject && (
          <Flex flexDirection="column" width="full" gap="1">
            <SingleSelector
              size="sm"
              isClearable
              isLoading={isLoadingPersons || accountsQuery.isFetching || organizationQuery.isLoading}
              placeholder={{ text: `${t("Select contact person")}...`, color: "black" }}
              value={selectedOption}
              options={options}
              inputValue={search}
              onInputChange={(value) => {
                setSearch(value);
                debounceSearch(value);
              }}
              onChange={onSelect}
            />
            {hasMoreResults && <FormHelperText mt="0">{t("Refine your search to see more results.")}</FormHelperText>}
            {failedQueries.length > 0 && (
              <HStack spacing="2">
                <Text fontSize="sm" color="smError">
                  {t("Some contact persons could not be loaded.")}
                </Text>
                <Button size="xs" variant="link" colorScheme="teal" onClick={retryFailedQueries} isLoading={isRetrying}>
                  {t("Try again")}
                </Button>
              </HStack>
            )}
            {canManageContacts && (
              <ContactPersonForm
                subject={subject}
                onSubmit={(contact) => selectContact(contact)}
                submitLabel={`${t("Save and select")}`}
                openLabel={`${t("+ New contact person")}`}
              />
            )}
          </Flex>
        )}
        {contractState.contract.contactPersonCopiedFromOriginal && (
          <CopiedFromOriginalNote text={t("The contact person from the original was copied.")} />
        )}
        <FormHelperText>{t("Provide contact info")}</FormHelperText>
        <FormErrorMessage>{errorMessage && errorMessage}</FormErrorMessage>
      </FormControl>
    </Box>
  );
};
